import { useState } from 'react'

type View = 'number' | 'list' | 'person'

const STEPS: { key: View; label: string; plain: string }[] = [
  { key: 'number', label: '1. The number', plain: 'What you see on the dashboard' },
  { key: 'list', label: '2. The list', plain: 'Every row behind that number' },
  { key: 'person', label: '3. The person', plain: 'One customer record' },
]

const SOURCE_BARS = [
  { label: 'Online marketplace', value: 96, width: '88%' },
  { label: 'Website quote form', value: 74, width: '66%' },
  { label: 'Partner referrals', value: 52, width: '44%' },
  { label: 'Phone calls in', value: 31, width: '26%' },
]

const REPORT_ROWS = [
  { source: 'Online marketplace', contacts: '118', connected: '81', outcomes: '34' },
  { source: 'Website quote form', contacts: '96', connected: '74', outcomes: '29' },
  { source: 'Partner referrals', contacts: '64', connected: '47', outcomes: '21' },
  { source: 'Phone calls in', contacts: '40', connected: '39', outcomes: '12' },
]

const RECORD_EVENTS = [
  { time: '9:12 am', event: 'Lead arrived from the marketplace' },
  { time: '10:34 am', event: 'Agent connected on a call' },
  { time: '10:41 am', event: 'Marked as quoted' },
  { time: '11:05 am', event: 'Application sent for signature' },
]

/**
 * Plain-language drill: dashboard number → list → one person.
 * Synthetic demo content; always labeled illustrative.
 */
export function SignalDrilldown() {
  const [view, setView] = useState<View>('number')
  const active = STEPS.find((s) => s.key === view) ?? STEPS[0]

  return (
    <div className="rounded-sm border border-base-line bg-base-900 p-4 sm:p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-dim">
        How a number opens
      </p>
      <ol className="mt-3 flex flex-col gap-2" aria-label="How a number opens">
        {STEPS.map((step) => (
          <li key={step.key}>
            <button
              type="button"
              aria-pressed={view === step.key}
              onClick={() => setView(step.key)}
              className={`flex w-full flex-col rounded-sm border px-3 py-2 text-left transition-colors duration-state ${
                view === step.key
                  ? 'border-signal/60 bg-signal/10'
                  : 'border-base-linebright hover:border-signal/30'
              }`}
            >
              <span
                className={`text-sm font-bold ${view === step.key ? 'text-signal' : 'text-ink'}`}
              >
                {step.label}
              </span>
              <span className="text-xs text-ink-mute">{step.plain}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="mt-5 min-h-[260px] border-t border-base-line pt-4">
        <p className="mb-3 text-xs text-ink-dim">{active.plain}</p>

        {view === 'number' && (
          <ul className="space-y-3.5" aria-label="Results by where work came from">
            {SOURCE_BARS.map((bar) => (
              <li key={bar.label}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold text-ink">{bar.label}</span>
                  <span className="font-mono text-[11px] tabular-nums text-signal">{bar.value}</span>
                </div>
                <span className="mt-1 block h-2 rounded-sm bg-base-800">
                  <span
                    className="block h-full rounded-sm bg-signal"
                    style={{ width: bar.width }}
                    aria-hidden="true"
                  />
                </span>
              </li>
            ))}
          </ul>
        )}

        {view === 'list' && (
          <div className="overflow-x-auto rounded-sm border border-base-line">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead className="bg-base-850 font-mono text-[10px] uppercase tracking-[0.12em] text-ink-dim">
                <tr>
                  <th className="px-3 py-2 font-medium">Where they came from</th>
                  <th className="px-3 py-2 font-medium">People</th>
                  <th className="px-3 py-2 font-medium">Reached</th>
                  <th className="px-3 py-2 font-medium">Won</th>
                </tr>
              </thead>
              <tbody>
                {REPORT_ROWS.map((row) => (
                  <tr key={row.source} className="border-t border-base-line">
                    <td className="px-3 py-2 text-ink">{row.source}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-ink-mute">{row.contacts}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-ink-mute">{row.connected}</td>
                    <td className="px-3 py-2 font-mono tabular-nums text-signal">{row.outcomes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {view === 'person' && (
          <div className="rounded-sm border border-base-line bg-base-850 p-4">
            <p className="text-sm font-extrabold text-ink">Jordan Hale · quote request</p>
            <p className="mt-1 text-xs text-ink-mute">From online marketplace · Assigned to your team</p>
            <ol className="mt-4 space-y-2">
              {RECORD_EVENTS.map((ev) => (
                <li key={ev.time} className="flex gap-3 text-sm">
                  <span className="w-16 shrink-0 font-mono text-[11px] text-ink-dim">{ev.time}</span>
                  <span className="text-ink-mute">{ev.event}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <p className="mt-3">
        <span className="illustrative-tag">Conceptual diagram · not product UI</span>
      </p>
    </div>
  )
}
